import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { ArrowLeft, Check, ShieldCheck, ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import BuyNowButton from "@/components/BuyNowButton";
import { getProduct, PRODUCTS } from "@/data/products";
import { Reveal, Eyebrow, MarqueeBand } from "@/components/editorial";
import { MagneticButton } from "@/components/MagneticButton";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const product = getProduct(slug);
  const heroRef = useRef(null);
  const reduce = useReducedMotion();

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.05]);

  if (!product) {
    return (
      <div data-testid="product-not-found" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
        <h1 className="font-display text-3xl text-brand-ink">Product not found.</h1>
        <Link to="/products" className="mt-6 inline-flex items-center gap-2 text-brand-sageDark font-medium">
          <ArrowLeft className="h-4 w-4" /> Back to products
        </Link>
      </div>
    );
  }

  const idx = PRODUCTS.findIndex((p) => p.slug === product.slug);
  const chapterNum = String(idx + 1).padStart(2, "0");
  const others = PRODUCTS.filter((p) => p.slug !== product.slug);

  return (
    <div data-testid="product-detail-page" className="relative">
      <Seo
        title={product.name}
        description={product.shortDesc}
        path={`/products/${product.slug}`}
        type="product"
      />

      {/* HERO */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pt-14 md:pt-20 pb-20 md:pb-24"
      >
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute inset-0 bg-brand-bg" />
          <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-brand-sage/12 blur-[110px]" />
          <div className="absolute inset-0 grain opacity-40" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-inkMuted hover:text-brand-ink transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All products
            </Link>
            <span className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-inkMuted hidden md:block">
              One Step Ahead, catalog
            </span>
          </div>

          <div className="mt-10 md:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Left: editorial image */}
            <div className="lg:col-span-6">
              <div className="relative">
                <div className="absolute -top-8 -left-2 md:-left-4 z-20 select-none pointer-events-none">
                  <span className="font-editorial italic text-brand-accent text-6xl md:text-8xl leading-none">
                    {chapterNum}
                  </span>
                </div>
                <motion.div
                  style={{ y: imgY, scale: imgScale }}
                  className="spotlight-frame relative rounded-[28px] overflow-hidden aspect-[4/5] w-full bg-brand-sage/10"
                >
                  <img
                    src={product.cover}
                    alt={product.name}
                    loading="eager"
                    className="h-full w-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-brand-ink/10 via-transparent to-transparent" />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 0.8 }}
                  className="absolute -right-3 top-6 rotate-90 origin-top-right hidden lg:block"
                >
                  <span className="text-[10px] uppercase tracking-[0.3em] text-brand-inkMuted">
                    Fig. {chapterNum}, cover
                  </span>
                </motion.div>
              </div>
            </div>

            {/* Right: title + buy */}
            <div className="lg:col-span-6">
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-sage">
                <Sparkles className="h-3.5 w-3.5" /> {product.tagline}
              </span>
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                className="mt-4 font-display font-medium tracking-[-0.02em] text-brand-ink text-4xl sm:text-5xl md:text-6xl leading-[1.02]"
              >
                {product.name.split(" ").map((w, i) =>
                  /system|kit|center/i.test(w) ? (
                    <span key={i} className="font-editorial italic font-normal text-brand-sage">
                      {w}{" "}
                    </span>
                  ) : (
                    <span key={i}>{w} </span>
                  )
                )}
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.85, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="mt-6 text-base sm:text-lg text-brand-inkSoft leading-relaxed max-w-xl"
              >
                {product.longDesc}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.85, delay: 0.45, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8 flex items-baseline gap-4"
              >
                <span className="font-display text-5xl font-semibold text-brand-ink tabular-nums">
                  ${product.price}
                </span>
                <span className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-inkMuted">
                  one-time, lifetime updates
                </span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.85, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="mt-6 flex flex-wrap items-center gap-3"
              >
                <MagneticButton>
                  <BuyNowButton
                    testid="detail-buy-now"
                    packageId={product.packageId}
                    label={`Buy now, $${product.price}`}
                  />
                </MagneticButton>
                <span className="inline-flex items-center gap-2 text-xs text-brand-inkMuted">
                  <ShieldCheck className="h-3.5 w-3.5 text-brand-sage" />
                  Secure Stripe checkout, instant access, 14-day refund
                </span>
              </motion.div>

              {/* What's included */}
              <div className="mt-12">
                <Eyebrow>What&apos;s included</Eyebrow>
                <ul className="mt-5 space-y-3">
                  {product.included.map((item, i) => (
                    <motion.li
                      key={item}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.7 + i * 0.06 }}
                      className="flex items-start gap-4 text-brand-ink"
                    >
                      <span className="mt-1 font-editorial italic text-brand-accent text-lg leading-none tabular-nums">
                        0{i + 1}
                      </span>
                      <span className="text-sm sm:text-base leading-relaxed">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INSIDE THE PRODUCT */}
      <section className="relative bg-brand-ink text-brand-bg overflow-hidden py-24 md:py-32">
        <div className="absolute inset-0 grain opacity-20 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-sage">
              Chapter {chapterNum}, inside the system
            </p>
            <h2 className="mt-4 font-display font-medium tracking-[-0.02em] text-4xl md:text-5xl leading-[1.02] max-w-3xl">
              What you get, <span className="font-editorial italic text-brand-accent">page by page.</span>
            </h2>
          </Reveal>

          <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
            {product.inside.map((item, i) => (
              <Reveal key={item} delay={i * 0.06}>
                <div className="group flex items-start gap-6 pb-6 border-b border-white/10">
                  <span className="font-editorial italic text-brand-accent text-4xl md:text-5xl leading-none tabular-nums shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="pt-1">
                    <h3 className="font-display text-xl md:text-2xl font-medium leading-snug">
                      {item}
                    </h3>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SNEAK PEEK GALLERY */}
      <section data-testid="sneak-peek" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div>
                <Eyebrow>Chapter {String(idx + 2).padStart(2, "0")}, a look inside</Eyebrow>
                <h2 className="mt-4 font-display font-medium tracking-[-0.02em] text-brand-ink text-4xl md:text-5xl leading-[1.02] max-w-2xl">
                  Sneak <span className="font-editorial italic text-brand-sage">peek.</span>
                </h2>
              </div>
              <p className="max-w-xs text-sm text-brand-inkMuted">
                Previews are a taste of the layout. Full templates and guides are delivered instantly after purchase.
              </p>
            </div>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {product.samples.map((src, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <figure
                  className={`relative rounded-[24px] overflow-hidden bg-brand-bg border border-brand-border aspect-[4/5] ${
                    i % 3 === 1 ? "sm:mt-10" : ""
                  } ${i % 3 === 2 ? "sm:mt-4" : ""}`}
                >
                  <img
                    src={src}
                    alt={`${product.name} preview ${i + 1}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-[900ms] hover:scale-[1.03]"
                  />
                  <figcaption className="absolute top-4 left-5 font-editorial italic text-brand-accent text-3xl leading-none select-none">
                    {String(i + 1).padStart(2, "0")}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* BUY BAND */}
      <section className="px-4 sm:px-6 lg:px-8 pb-24">
        <Reveal>
          <div className="max-w-6xl mx-auto rounded-[32px] overflow-hidden bg-gradient-to-br from-brand-sage to-brand-sageDark text-white relative">
            <div className="absolute inset-0 grain opacity-25 pointer-events-none" />
            <div className="relative p-10 md:p-16 text-center">
              <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-white/85">
                Ready to feel ahead?
              </p>
              <h2 className="mt-4 font-display font-medium tracking-[-0.02em] text-4xl sm:text-5xl md:text-6xl leading-[1.02] max-w-3xl mx-auto">
                Get instant access to the <span className="font-editorial italic">{product.name.replace(/^The /, "")}</span>
              </h2>
              <p className="mt-5 text-white/85 max-w-lg mx-auto">
                Print + digital, free lifetime updates, and a 14-day refund window.
              </p>
              <div className="mt-8 flex justify-center">
                <MagneticButton>
                  <BuyNowButton
                    testid="detail-buy-now-band"
                    packageId={product.packageId}
                    label={`Buy now, $${product.price}`}
                    variant="ghost"
                  />
                </MagneticButton>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <MarqueeBand items={["Print + Digital", "Instant Access", "Lifetime Updates", "Real School Life", "Systems, Not Memory"]} />

      {/* OTHER SYSTEMS */}
      {others.length > 0 && (
        <section className="py-24 md:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <div className="flex items-end justify-between gap-6 mb-12">
                <div>
                  <Eyebrow>More systems</Eyebrow>
                  <h2 className="mt-4 font-display font-medium tracking-[-0.02em] text-brand-ink text-3xl md:text-4xl">
                    Keep reading.
                  </h2>
                </div>
                <Link
                  to="/products"
                  className="inline-flex items-center gap-2 text-sm font-medium text-brand-sageDark hover:text-brand-ink transition-colors"
                >
                  All products <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {others.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.1}>
                  <Link
                    to={`/products/${p.slug}`}
                    className="group block rounded-[24px] overflow-hidden bg-white border border-brand-border"
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-5 gap-0">
                      <div className="sm:col-span-2 aspect-[4/3] sm:aspect-auto overflow-hidden">
                        <img
                          src={p.cover}
                          alt={p.name}
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                        />
                      </div>
                      <div className="sm:col-span-3 p-6 md:p-8">
                        <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-brand-sage">{p.tagline}</p>
                        <h3 className="mt-3 font-display text-xl md:text-2xl font-medium text-brand-ink leading-snug group-hover:text-brand-sageDark transition-colors">
                          {p.name}
                        </h3>
                        <div className="mt-6 flex items-center justify-between">
                          <span className="font-display text-lg font-semibold text-brand-ink tabular-nums">${p.price}</span>
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-brand-sageDark">
                            Read the system <ArrowRight className="h-3.5 w-3.5" />
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
